import { useState, useEffect } from 'react';

export default function CurrenciesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', symbol: '', decimal_places: '2' });

  async function load() {
    setLoading(true);
    try {
      const r = await fetch('/entities/currencies');
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to load currencies');
      setItems(d.data?.items || d.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.code || !form.name) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch('/entities/currencies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: form.code.toUpperCase(),
          name: form.name,
          symbol: form.symbol,
          decimal_places: parseInt(form.decimal_places, 10) || 2,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to create currency');
      setForm({ code: '', name: '', symbol: '', decimal_places: '2' });
      setShowForm(false);
      await load();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="entities-currencies-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Currencies</h1>
          <p className="muted">Manage all currencies used across legal entities.</p>
        </div>
        <button className="btn primary" data-testid="entity-currency-add" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'Add Currency'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="entities-currencies-error">{error}</div>}

      {showForm && (
        <form onSubmit={handleAdd} className="card form-card row-form">
          <label>Code
            <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="e.g. USD" maxLength={3} />
          </label>
          <label>Name
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. US Dollar" />
          </label>
          <label>Symbol
            <input value={form.symbol} onChange={(e) => setForm((f) => ({ ...f, symbol: e.target.value }))} placeholder="e.g. $" maxLength={5} />
          </label>
          <label>Decimal places
            <input type="number" min="0" max="4" value={form.decimal_places} onChange={(e) => setForm((f) => ({ ...f, decimal_places: e.target.value }))} />
          </label>
          <div className="actions">
            <button className="btn primary" type="submit" disabled={busy || !form.code || !form.name}>Save</button>
          </div>
        </form>
      )}

      <div className="card">
        <table className="data-table" data-testid="entity-currency-list">
          <thead>
            <tr><th>Code</th><th>Name</th><th>Symbol</th><th>Decimals</th><th>Active</th></tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`entity-currency-row-${c.id}`}>
                <td><strong>{c.code}</strong></td>
                <td>{c.name}</td>
                <td>{c.symbol || '—'}</td>
                <td>{c.decimal_places ?? 2}</td>
                <td>{c.active !== false ? 'yes' : 'no'}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="muted">No currencies configured.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
